import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { keyframes } from "@emotion/react";
import MicIcon from "@mui/icons-material/Mic";
import CloseIcon from "@mui/icons-material/Close";
// Local files
import BaseDialog from "components/Common/BaseDialog/BaseDialog";
import useCustomSelector from "hooks/useCustomSelector";
import useDialogs from "hooks/useDialogs";
import useSuccess from "hooks/useSuccess";
import useOrders from "hooks/useOrders";
import useVoiceCommand from "hooks/useVoiceCommand";
import {
  VOICE_API_TIMEOUT_MS,
  VOICE_IDLE_GUARD_MS,
} from "./constants";

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const getRequestErrorMessage = (err) => {
  const data = err?.response?.data;
  const fromData =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error?.message === "string" && data.error.message) ||
    (typeof data?.error === "string" && data.error) ||
    (typeof data === "string" && data);
  return (
    fromData ||
    err?.message ||
    "Something went wrong. Please try again."
  );
};

const buildVoiceTranscriptForOrder = (orderNumber, spokenTrimmed) => {
  const normalizedOrderNumber = String(orderNumber || "N/A").trim();
  return `${spokenTrimmed} | Order Number: ${normalizedOrderNumber} | Task: update order status`;
};

const VoiceCommand = () => {
  const { id: routeOrderId } = useParams();
  const { closeVoiceCommandDialog } = useDialogs();
  const { setSuccess } = useSuccess();
  const { userOrderTranscript } = useOrders();
  const currentOrder = useCustomSelector((state) => state.orders.order);
  const { open, orderNumber: dialogOrderNumber } = useCustomSelector(
    (state) => state.dialogs.voiceCommand,
  );

  /** Prefer loaded order `number` when URL id matches (same as order card title); URL is order id, not display number. */
  const orderNumber = useMemo(() => {
    if (
      routeOrderId != null &&
      routeOrderId !== "" &&
      currentOrder?.id != null &&
      String(currentOrder.id) === String(routeOrderId)
    ) {
      return currentOrder.number ?? dialogOrderNumber;
    }
    return dialogOrderNumber;
  }, [
    routeOrderId,
    currentOrder?.id,
    currentOrder?.number,
    dialogOrderNumber,
  ]);

  const isSubmittingRef = useRef(false);
  const initializedForOpenRef = useRef(false);
  const voiceSessionActiveRef = useRef(false);
  const suppressVoiceFinalizeRef = useRef(false);
  const apiTimeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);

  const [apiError, setApiError] = useState("");
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);

  const clearApiTimeout = useCallback(() => {
    if (apiTimeoutRef.current) {
      clearTimeout(apiTimeoutRef.current);
      apiTimeoutRef.current = null;
    }
  }, []);

  const clearSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  voiceSessionActiveRef.current = open;

  const handleFinalTranscript = useCallback(
    (spokenText) => {
      if (!open || isSubmittingRef.current) {
        return;
      }

      const trimmed = String(spokenText || "").trim();
      if (!trimmed) {
        setApiError("No speech detected. Press the mic button to restart.");
        return;
      }

      isSubmittingRef.current = true;
      setApiError("");
      setIsTranscriptLoading(true);
      clearApiTimeout();

      const transcriptPayload = buildVoiceTranscriptForOrder(
        orderNumber,
        trimmed,
      );

      const requestPromise = userOrderTranscript({ transcript: transcriptPayload });
      const timeoutPromise = new Promise((_, reject) => {
        apiTimeoutRef.current = setTimeout(() => {
          reject(new Error("Voice request timed out. Please try again."));
        }, VOICE_API_TIMEOUT_MS);
      });

      Promise.race([requestPromise, timeoutPromise])
        .then((successAction) => {
          const data = successAction?.payload?.response?.data;

          if (data?.success === false && data?.error) {
            const err = data.error;
            const msg =
              (typeof err === "object" &&
                err !== null &&
                typeof err.message === "string" &&
                err.message) ||
              (typeof err === "string" && err) ||
              "Something went wrong. Please try again.";
            setApiError(msg);
            return;
          }
          setSuccess("Order updated successfully.");
          closeVoiceCommandDialog();
        })
        .catch((err) => {
          setApiError(getRequestErrorMessage(err));
        })
        .finally(() => {
          isSubmittingRef.current = false;
          clearApiTimeout();
          setIsTranscriptLoading(false);
        });
    },
    [
      open,
      orderNumber,
      userOrderTranscript,
      setSuccess,
      closeVoiceCommandDialog,
      clearApiTimeout,
    ],
  );

  const {
    isListening,
    transcript,
    error,
    startListening,
    stopListening,
    reset,
    isSupported,
  } = useVoiceCommand({
    onFinalTranscript: handleFinalTranscript,
    isSessionActiveRef: voiceSessionActiveRef,
    suppressFinalizeRef: suppressVoiceFinalizeRef,
  });

  useEffect(() => {
    if (!open) {
      stopListening();
      initializedForOpenRef.current = false;
      isSubmittingRef.current = false;
      setApiError("");
      setIsTranscriptLoading(false);
      clearApiTimeout();
      clearSilenceTimeout();
      return;
    }

    if (initializedForOpenRef.current || !isSupported) {
      return;
    }

    initializedForOpenRef.current = true;
    if (isSupported) {
      suppressVoiceFinalizeRef.current = false;
      reset();
      isSubmittingRef.current = false;
      setApiError("");
      startListening();
    }
  }, [
    open,
    isSupported,
    reset,
    startListening,
    stopListening,
    clearApiTimeout,
    clearSilenceTimeout,
  ]);

  useEffect(() => {
    if (!open || !isListening || !!transcript || isTranscriptLoading || !!error) {
      clearSilenceTimeout();
      return;
    }

    clearSilenceTimeout();
    silenceTimeoutRef.current = setTimeout(() => {
      if (isListening) {
        stopListening();
      }
      setApiError("No speech detected. Press the mic button to restart.");
    }, VOICE_IDLE_GUARD_MS);

    return clearSilenceTimeout;
  }, [
    open,
    isListening,
    transcript,
    isTranscriptLoading,
    error,
    stopListening,
    clearSilenceTimeout,
  ]);

  const handleClose = () => {
    suppressVoiceFinalizeRef.current = true;
    clearApiTimeout();
    clearSilenceTimeout();
    if (isListening) {
      stopListening();
    }
    closeVoiceCommandDialog();
  };

  const handleMicRestart = () => {
    setApiError("");
    isSubmittingRef.current = false;
    suppressVoiceFinalizeRef.current = false;
    clearSilenceTimeout();
    reset();
    startListening();
  };

  const showSpeechMicError = !!error;
  const showMicForRetry =
    (showSpeechMicError || !!apiError) && !isTranscriptLoading;
  const showListeningMic = isListening && !isTranscriptLoading;
  const showIdleMic = !isListening && !showMicForRetry && !isTranscriptLoading;

  if (!isSupported) {
    return (
      <BaseDialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: 400, borderRadius: "16px" } }}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}>
            <Typography variant="h6">Voice Command</Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.text.secondary,
              }}>
              <CloseIcon />
            </IconButton>
          </Box>
        }>
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="error">
              Voice recognition is not supported in this browser.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please use Chrome, Edge, or Safari for voice commands.
            </Typography>
          </Box>
        </DialogContent>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: 400, borderRadius: "16px" } }}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}>
          <Typography variant="h6">Voice Command</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.text.secondary,
            }}>
            <CloseIcon />
          </IconButton>
        </Box>
      }>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
          }}>
          {isTranscriptLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 2,
                width: "100%",
              }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}>
                Processing your request…
              </Typography>
            </Box>
          )}

          {!isTranscriptLoading && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}>
              {showListeningMic && (
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    backgroundColor: "error.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: `${pulse} 1.5s ease-in-out infinite`,
                  }}>
                  <MicIcon sx={{ fontSize: 60, color: "error.main" }} />
                </Box>
              )}
              {showMicForRetry && (
                <Box
                  onClick={handleMicRestart}
                  role="button"
                  aria-label="restart voice command"
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    backgroundColor: "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}>
                  <MicIcon sx={{ fontSize: 60, color: "error.main" }} />
                </Box>
              )}
              {showIdleMic && (
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    backgroundColor: "primary.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <MicIcon sx={{ fontSize: 60, color: "primary.main" }} />
                </Box>
              )}
            </Box>
          )}

          {!isTranscriptLoading && (
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: isListening
                  ? "error.main"
                  : error || apiError
                    ? "error.main"
                    : "text.primary",
                fontWeight: 600,
                textAlign: "center",
              }}>
              {isListening
                ? "Listening..."
                : error
                  ? "Error occurred"
                  : apiError
                    ? "Could not process"
                    : transcript
                      ? "Got it"
                      : "Tap mic and speak"}
            </Typography>
          )}

          {transcript && (
            <Box
              sx={{
                width: "100%",
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: "grey.100",
                minHeight: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Typography
                variant="body1"
                sx={{ fontStyle: "italic", color: "text.secondary" }}>
                &ldquo;{String(transcript).trim()}&rdquo;
              </Typography>
            </Box>
          )}

          {error && !isTranscriptLoading && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1, textAlign: "center", px: 1 }}>
              {error === "no-speech"
                ? "No speech detected. Press the mic button to restart."
                : error === "audio-capture"
                  ? "Microphone not accessible. Check permissions and press the mic button to restart."
                  : error === "not-allowed"
                    ? "Microphone permission denied. Enable it in browser settings, then press the mic button to restart."
                    : `Error: ${error}`}
            </Typography>
          )}

          {apiError && !isTranscriptLoading && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 2, textAlign: "center", px: 1 }}>
              {apiError}
              {showMicForRetry && (
                <Typography
                  component="span"
                  variant="body2"
                  display="block"
                  sx={{ mt: 1 }}>
                  Press the mic button to try again.
                </Typography>
              )}
            </Typography>
          )}

          {!transcript &&
            !error &&
            !apiError &&
            !isTranscriptLoading &&
            (isListening || showIdleMic) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: "center", px: 2 }}>
                Say what you would like to do with this order.
              </Typography>
            )}
        </Box>
      </DialogContent>
    </BaseDialog>
  );
};

export default memo(VoiceCommand);
